/**
 * Customer-facing quote accept/decline page — Phase 2J-B.
 *
 * Routes:
 *   /quote/:id/accept           — requires logged-in customer or admin
 *   /quote/:id/accept?token=…   — works without auth via signed token
 */
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Check, X, Shield, ArrowRight, FileText } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { BrandLogo } from "@/components/BrandLogo";

export default function QuoteAcceptPage() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const token = sp.get("token");
  const [quote, setQuote] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [declineMode, setDeclineMode] = useState(false);
  const [reason, setReason] = useState("");

  useEffect(() => {
    const url = token ? `/pristine/quote-requests/${id}/public?token=${encodeURIComponent(token)}` : `/pristine/quote-requests/${id}/public`;
    api.get(url).then(({ data }) => setQuote(data)).catch((e) => setErr(e.response?.data?.detail || "Quote not found"));
  }, [id, token]);

  const action = async (kind) => {
    setBusy(true);
    try {
      const body = { token: token || undefined, reason: kind === "decline" ? (reason || null) : undefined };
      const { data } = await api.post(`/pristine/quote-requests/${id}/${kind}`, body);
      setQuote(data);
      toast.success(kind === "accept" ? "Quote accepted — thank you" : "Quote declined");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };

  if (err) return <Wrap><p data-testid="quote-accept-error" className="text-rose-600 text-sm">{err}</p></Wrap>;
  if (!quote) return <Wrap><p className="text-sm text-slate-500">Loading…</p></Wrap>;

  const items = quote.line_items || [];
  const included = items.filter((i) => i.included);
  const terminal = ["accepted", "declined"].includes(quote.status);

  return (
    <Wrap>
      <div data-testid="quote-accept-page" className="max-w-3xl mx-auto">
        <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-700 flex items-center gap-1.5">
          <Shield className="w-3.5 h-3.5" /> Pristine Water Systems
        </div>
        <h1 className="font-display text-3xl lg:text-4xl font-bold text-slate-900 mt-2">Your tailored quote</h1>
        <p className="mt-2 text-sm text-slate-600">
          Ref <code className="font-mono">{quote.id.slice(0, 8).toUpperCase()}</code> · Valid until {quote.valid_until || "—"}
        </p>

        <div className="mt-6 rounded-3xl bg-white border border-slate-200 overflow-hidden">
          <ul className="divide-y divide-slate-100">
            {included.map((li) => (
              <li key={li.key} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`accept-line-${li.key}`}>
                <div>
                  <div className="font-semibold text-slate-900 text-sm">
                    {li.name}
                    {li.variant_label && <span className="ml-1.5 text-[10px] font-bold uppercase tracking-wider text-cyan-700 bg-cyan-100 rounded px-1.5 py-0.5">{li.variant_label}</span>}
                  </div>
                  <div className="text-xs text-slate-500">{li.description}</div>
                </div>
                <div className="text-sm tabular-nums text-slate-900 text-right whitespace-nowrap">
                  {li.qty}× ${(li.price).toFixed(2)}<br/>
                  <span className="text-xs text-slate-500">${(li.price * li.qty).toFixed(2)}</span>
                </div>
              </li>
            ))}
          </ul>
          <div className="px-5 py-4 border-t border-slate-100 bg-slate-50/60 flex items-center justify-between">
            <span className="text-sm font-semibold text-slate-700">Total (AUD)</span>
            <span className="font-display text-2xl font-bold text-slate-900 tabular-nums" data-testid="accept-total">
              ${(quote.totals?.total || 0).toFixed(2)}
            </span>
          </div>
        </div>

        {terminal ? (
          <div className="mt-6 rounded-2xl bg-slate-50 border border-slate-200 p-5 text-center">
            <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center ${quote.status === "accepted" ? "bg-emerald-100" : "bg-rose-100"}`}>
              {quote.status === "accepted" ? <Check className="w-6 h-6 text-emerald-600" /> : <X className="w-6 h-6 text-rose-600" />}
            </div>
            <p className="mt-3 text-sm font-semibold text-slate-900">
              {quote.status === "accepted"
                ? "Quote accepted — a Pristine Water Systems specialist will be in touch shortly."
                : "Quote declined — thank you for letting us know."}
            </p>
          </div>
        ) : !declineMode ? (
          <div className="mt-6 flex flex-wrap gap-3">
            <Button onClick={() => action("accept")} disabled={busy} data-testid="accept-btn"
              className="rounded-full bg-cyan-600 hover:bg-cyan-500 text-white px-6 py-5">
              <Check className="w-4 h-4 mr-1.5" /> Accept this quote <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
            <Button onClick={() => setDeclineMode(true)} disabled={busy} variant="outline" data-testid="decline-mode-btn"
              className="rounded-full px-5 py-5">
              <X className="w-4 h-4 mr-1.5" /> Decline
            </Button>
            <a href={`/api/pristine/quote-requests/${id}/quote.pdf`} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-semibold text-slate-700 hover:bg-slate-100 transition-colors">
              <FileText className="w-4 h-4" /> Download PDF
            </a>
          </div>
        ) : (
          <div className="mt-6 rounded-2xl bg-white border border-slate-200 p-5">
            <p className="text-sm font-semibold text-slate-900">Anything we should know?</p>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)}
              rows={3} maxLength={1000} data-testid="decline-reason"
              placeholder="Optional feedback — pricing, timing, scope…"
              className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm" />
            <div className="mt-3 flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => setDeclineMode(false)} className="rounded-full">Cancel</Button>
              <Button onClick={() => action("decline")} disabled={busy} data-testid="decline-btn"
                className="rounded-full bg-rose-600 hover:bg-rose-500 text-white">
                Confirm decline
              </Button>
            </div>
          </div>
        )}

        <p className="mt-6 text-[11px] text-slate-500 leading-relaxed">
          This quote aims to help improve your water quality and assist in addressing the recommendations made by
          Aware Water Testing. Acceptance is a commitment to proceed with the included items; final scope,
          scheduling and any property-specific details will be confirmed by your local Pristine Water Systems specialist.
        </p>
      </div>
    </Wrap>
  );
}


function Wrap({ children }) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white pt-8 pb-16 px-4 sm:px-6 lg:px-8">
      <header className="max-w-3xl mx-auto mb-10 flex items-center justify-between">
        <BrandLogo to="/" size="md" />
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400 hidden sm:block">Quote · Pristine Water Systems</div>
      </header>
      {children}
    </div>
  );
}
